/**
 * StationCard copy set. The package is domain- and locale-agnostic: its
 * components accept their user-facing copy through an optional `copy` prop
 * of this shape and default to the English {@link defaultStationCardCopy}
 * for standalone use. The metro-frontend host builds a localised set from
 * the `station-card` catalog (`buildStationCardCopy(t)`) and threads
 * it down, so the en render is byte-identical and snapshots are unchanged.
 */
export interface StationCardCopySet {
  /** From action button label. */
  readonly from: string
  /** To action button label. */
  readonly to: string
  /** "Station information" secondary button label. */
  readonly stationInformation: string
  /** Not-found empty-state title. */
  readonly notFound: string
  /** Not-found retry button label. */
  readonly retry: string
  /** Loading overlay accessible label. */
  readonly loadingAria: string
  /** Carousel card accessible label — `Station {{index}} of {{total}}: {{name}}`. */
  readonly carouselCardAria: (index: number, total: number, name: string) => string
  /** Closed status-banner title. */
  readonly statusClosedTitle: string
  /** Closed status-banner subtitle. */
  readonly statusClosedSubtitle: string
  /** Restricted status-banner title. */
  readonly statusRestrictedTitle: string
  /** Restricted status-banner subtitle. */
  readonly statusRestrictedSubtitle: string
  /** Advisory status-banner title. */
  readonly statusAdvisoryTitle: string
  /** Advisory status-banner subtitle. */
  readonly statusAdvisorySubtitle: string
}

export const defaultStationCardCopy: StationCardCopySet = {
  from: 'From here',
  to: 'To here',
  stationInformation: 'Station information',
  notFound: 'Station not found',
  retry: 'Retry',
  loadingAria: 'Loading station details',
  carouselCardAria: (index: number, total: number, name: string) =>
    `Station ${index} of ${total}: ${name}`,
  statusClosedTitle: 'Station closed',
  statusClosedSubtitle: 'All day',
  statusRestrictedTitle: 'Limited service',
  statusRestrictedSubtitle: 'Step-free access only',
  statusAdvisoryTitle: 'Service advisory',
  statusAdvisorySubtitle: 'Tap for more info',
}
